import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Input } from '../components/ui/atoms/Input';
import { Label } from '../components/ui/label';
import { Button, getButtonClasses } from '@/components/ui/atoms/Button';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthClientError, confirmPasswordReset } from '../services/auth.service';

const MIN_PASSWORD = 12;

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValid = password.length >= MIN_PASSWORD;
  const formValid = passwordValid && password === confirm && Boolean(token);

  const VALUE_PROPS = [
    { text: t('auth:reset.value_prop_1') },
    { text: t('auth:reset.value_prop_2') },
    { text: t('auth:reset.value_prop_3') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset({ token, newPassword: password });
      toast.success(t('auth:reset.success'));
      navigate('/login', { replace: true });
    } catch (err) {
      const msg =
        err instanceof AuthClientError
          ? err.message
          : t('auth:reset.error_expired');
      setError(msg);
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout
        planTitle={t('auth:reset.plan_title_broken')}
        planTagline={t('auth:reset.plan_tagline')}
        planFeatures={VALUE_PROPS}
        description={t('auth:reset.plan_description_broken')}
      >
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-dark-invirtus">
            {t('auth:reset.title_broken')}
          </h1>
          <p className="text-sm text-dark-invirtus/60">
            {t('auth:reset.subtitle_broken')}
          </p>
          <Link
            to="/forgot-password"
            className={getButtonClasses(
              'contained',
              'secondary',
              'md',
              false,
              'w-full justify-center no-underline',
            )}
          >
            {t('auth:reset.request_new_link')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      planTitle={t('auth:reset.plan_title')}
      planTagline={t('auth:reset.plan_tagline')}
      planFeatures={VALUE_PROPS}
      description={t('auth:reset.plan_description')}
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-invirtus">
            {t('auth:reset.title')}
          </h1>
          <p className="mt-1 text-sm text-dark-invirtus/60">
            {t('auth:reset.subtitle')}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="new-password" className="text-dark-invirtus">
              {t('common:forms.new_password')}
            </Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              rounded="lg"
            />
            {password.length > 0 && !passwordValid && (
              <p className="text-xs text-dark-invirtus/60">
                {t('auth:reset.password_weak')}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-password" className="text-dark-invirtus">
              {t('common:forms.confirm_password')}
            </Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              rounded="lg"
            />
            {confirm.length > 0 && confirm !== password && (
              <p className="text-xs text-red-600">{t('auth:reset.passwords_no_match')}</p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <Button
            label={submitting ? t('auth:reset.updating') : t('auth:reset.update')}
            type="contained"
            intent="secondary"
            htmlType="submit"
            disabled={!formValid || submitting}
            loading={submitting}
            className="w-full"
          />
        </form>
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
